import { useCallback, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import { ModalInput } from '@/src/elements/input/ModalInput/ModalInput'
import { SelectInput } from '@/src/elements/select-input/SelectInput'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { useSettingsMutation } from '@/src/features/settings/services/useSettingsMutation'
import { TEST_ID } from '@/src/general/constants/testId'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { useTranslate, type TMessageKey } from '@/src/lang/useTranslate'

import { SettingsCard } from './SettingsCard'

import { locales } from '@/translations/translations.constants'

const localeKeyMap: Record<TLocale, TMessageKey> = {
  fr: 'language.fr',
  en: 'language.en',
  es: 'language.es',
  pt: 'language.pt',
  it: 'language.it',
  pl: 'language.pl',
}

const localesList = locales.filter((value) => value !== 'pl' && value !== 'it')

export const SettingsLanguageInput = (): ReactNode => {
  const { t } = useTranslate()
  const { data: me } = useMeQuery()
  const { mutateAsync } = useSettingsMutation()
  const [lang, setLang] = useState<TLocale | ''>(me?.lang ?? '')

  useOnChange(me?.lang, () => {
    if (me?.lang) setLang(me.lang)
  })

  const handleUncaughtSubmit = useCallback(async () => {
    if (!lang) return
    await mutateAsync({ lang })
  }, [lang, mutateAsync])

  return (
    <ModalInput
      testID={TEST_ID.SETTINGS.LANGUAGE.MODAL}
      renderTrigger={({ onTrigger }) => (
        <SettingsCard onPress={onTrigger} testID={TEST_ID.SETTINGS.LANGUAGE.INPUT}>
          <Typography size="xs">
            <FormattedMessage id="settings.language.title" />
          </Typography>
          <Typography bold>{me?.lang && <FormattedMessage id={localeKeyMap[me.lang]} />}</Typography>
        </SettingsCard>
      )}
      title={t('settings.language.title')}
      onUncaughtSubmit={handleUncaughtSubmit}
      confirmButtonProps={{ testID: TEST_ID.SETTINGS.LANGUAGE.CONFIRM, variant: 'faded' }}
    >
      <SelectInput<TLocale | ''>
        testID={TEST_ID.SETTINGS.LANGUAGE.SELECT}
        radius="full"
        items={localesList.map((value) => ({ label: t(localeKeyMap[value]), value }))}
        value={lang}
        onValueChange={(value) => setLang(value || '')}
      />
    </ModalInput>
  )
}
